import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Leaf, Wind, Archive, Droplets, Zap, HardHat, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const iconMap = { Leaf, Wind, Archive, Droplets, Zap, HardHat };

export default function Services() {
  const { lang, t } = useLanguage();
  const [services, setServices] = useState([]);

  useEffect(() => {
    document.title = `${t('services.title')} | Orange Onderhoudsbedrijf`;
    axios.get(`${API}/services`).then(r => setServices(r.data)).catch(() => {});
  }, [lang]);

  return (
    <div className="bg-[#F8FAFC] pt-20">
      {/* Page Header */}
      <section data-testid="services-header" className="bg-white border-b border-slate-100 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Orange Onderhoudsbedrijf</span>
          <h1 className="text-5xl md:text-6xl font-black text-[#0F172A] mt-3 mb-5">{t('services.title')}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-2xl">{t('services.subtitle')}</p>
        </div>
      </section>

      {/* Uniform 3-column Grid */}
      <section data-testid="services-grid" className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const Icon = iconMap[service.icon] || Leaf;
            return (
              <div
                key={service.id}
                data-testid={`service-item-${service.id}`}
                className="group bg-white border border-slate-100 rounded-2xl overflow-hidden hover:border-orange-200 hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                {/* Image with title overlay */}
                <div className="relative h-56 overflow-hidden flex-shrink-0">
                  <img
                    src={service.image_url}
                    alt={service.name?.[lang]}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=60'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-md">
                    <Icon size={22} strokeWidth={1.5} className="text-[#FF5F00]" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h2 className="font-bold text-white text-xl">{service.name?.[lang]}</h2>
                  </div>
                </div>

                {/* Text content — full description + pinned CTA */}
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[#64748B] text-sm leading-relaxed flex-1 mb-4">
                    {service.description?.[lang]}
                  </p>
                  <div className="pt-4 border-t border-slate-100">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 text-[#FF5F00] font-bold text-sm hover:gap-3 transition-all"
                    >
                      {t('services.quote')} <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 pb-20">
        <div className="bg-[#0F172A] rounded-3xl p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">{t('home.ctaTitle')}</h2>
            <p className="text-slate-400">{t('home.ctaSubtitle')}</p>
          </div>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-[#FF5F00] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#EA580C] transition-all whitespace-nowrap"
          >
            {t('home.ctaButton')} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
